import classNames from "classnames"
import {
  DeviceCropItemBoxProps,
  SessionCropBoxProps,
} from "@/app/activity/components/deviceTimeline/deviceCropTimeline/deviceCropItemBox/types.ts"
import { useCallback, useEffect, useMemo, useState } from "react"
import { colors } from "@/components/colors"
import { select } from "d3-selection"
import {
  createTextDot,
  getXPositionByDate,
} from "@/app/activity/components/timeline/activityTimeline/utils"
import { createActivityCommonStore, createSessionStore } from "@/app/activity/store"

import "./deviceCropItemBox.scss"
import SessionCropBox from "@/app/activity/components/deviceTimeline/deviceCropTimeline/deviceCropItemBox/SessionCropBox.tsx"

export default function DeviceCropItemBox({
  id = "activity-box",
  name = "",
  hoveredEventId,
  editable = false,
  hoverable = true,
  height = 35,
  width = 14,
  startX = 0,
  startY = 68,
  textId = "activity-box",
  rangeKey = "1m",
  idfData,
  onMouseLeave,
  onMouseOver,
  cropData,
  onSessionBoxClick,
  onCropBoxClick,
  idfSession,
}: DeviceCropItemBoxProps) {
  // Selected-session band — match the non-crop band (DataBox): Secondary/Blue/500 @ 30%
  const strokeColor = colors.blue[500]
  const fillColor = colors.blue[500]

  const [hovered, setHovered] = useState<boolean>(false)

  const { remainingArea, timelineWidth } = createActivityCommonStore((state) => ({
    remainingArea: state.remainingArea,
    timelineWidth: state.timelineWidth,
  }))

  const { selectedSession } = createSessionStore((state) => ({
    selectedSession: state.session,
  }))

  const transform = useMemo(() => {
    const x = isNaN(startX) ? 0 : startX

    const remain = (remainingArea ?? 0) / 2

    return `translate(${x + remain}, ${startY})`
  }, [startX, startY, remainingArea])

  const calculatedWidth = useMemo(() => {
    if (isNaN(width) || width <= 0) {
      return 14
    }

    return Math.max(width, rangeKey === "24h" ? 1 : 14)
  }, [width, rangeKey])

  const updateNameByWidth = useCallback(() => {
    select(`#_${textId}`).call(createTextDot, calculatedWidth, name)
  }, [calculatedWidth, name, textId])

  const sessionCropDataEach = useMemo(() => {
    return cropData.map((data) => {
      let { cropStartTime, cropEndTime } = data

      const sessionStart = new Date(selectedSession?.dateStart as string)
      const sessionEnd = new Date(selectedSession?.dateFinish as string)

      cropStartTime =
        typeof cropStartTime === "number"
          ? new Date(cropStartTime * 1000).toISOString()
          : cropStartTime
      cropEndTime =
        typeof cropEndTime === "number" ? new Date(cropEndTime * 1000).toISOString() : cropEndTime

      const startPoint =
        getXPositionByDate(new Date(cropStartTime), sessionStart, sessionEnd) * timelineWidth
      const endPoint =
        getXPositionByDate(new Date(cropEndTime), sessionStart, sessionEnd) * timelineWidth

      return {
        ...data,
        startX: startPoint,
        endX: endPoint,
        width: endPoint - startPoint,
      } as Partial<SessionCropBoxProps>
    })
  }, [cropData, timelineWidth, selectedSession])

  useEffect(() => {
    setHovered(hoveredEventId === id)
  }, [hoveredEventId, id])

  useEffect(() => {
    setTimeout(() => updateNameByWidth(), 0)
  }, [width, name])

  return (
    <>
      <g
        className="ActivityCropItemBox"
        transform={transform}
        onMouseOver={hoverable ? onMouseOver : undefined}
        onMouseLeave={hoverable ? onMouseLeave : undefined}
        onClick={onSessionBoxClick}>
        <rect
          className={classNames({ editable, hovered })}
          width={calculatedWidth}
          height={height}
          rx="0.2em"
          ry="0.2em"
          fill={fillColor}
          fillOpacity={0.3}
          style={{ stroke: strokeColor, strokeOpacity: 0.3, strokeWidth: 1 }}
        />
      </g>

      {sessionCropDataEach.map((data) => (
        <SessionCropBox
          {...(data as SessionCropBoxProps)}
          key={data.localIdfCropNumber || data.idfSessionCropRecord}
          startX={(data?.startX as number) + (remainingArea || 0) / 2}
          idfData={idfData}
          idfSession={idfSession}
          onClick={onCropBoxClick}
        />
      ))}
    </>
  )
}
